/**
 * /submit — path 2: Yahoo / ESPN / anywhere else.
 *
 * A person reads every one of these, so the form asks only for what a human
 * transcriber cannot recover from a screenshot: which platform, room size,
 * budget, format, and when it happened. Everything else is the upload.
 *
 * 🔴 FILES GO UP ONE AT A TIME, sequentially. The endpoint takes one file per
 * request precisely so a phone on hotel wifi retries a single 2MB screenshot
 * instead of losing an 8MB batch at 95%. The progress line exists because that
 * loop is otherwise invisible and people close the tab.
 */

import { useRef, useState } from "react";
import { captureResultsSubmitted } from "../../utils/submitAnalytics.js";
import { C, SZ, card, input, label, primaryBtn, fineText, successText, errorText } from "./submitStyles.js";

const MAX_FILES = 8;
const MAX_FILE_BYTES = 4 * 1024 * 1024;

// Mirrors ALLOWED_TYPES in api/submit-results.js. Client-side it is a courtesy
// — a clear message beats a 400 — so the server keeps its own copy as the one
// that actually decides.
const ACCEPT = ".png,.jpg,.jpeg,.webp,.csv,.xlsx,image/png,image/jpeg,image/webp,text/csv," +
  "application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet";

const PLATFORMS = [
  { value: "yahoo", label: "Yahoo" },
  { value: "espn", label: "ESPN" },
  { value: "other", label: "Somewhere else" },
];

const FORMATS = [
  { value: "1qb", label: "1 QB" },
  { value: "sflex", label: "Superflex" },
  { value: "2qb", label: "2 QB" },
];

// crypto.randomUUID is unavailable on older iOS Safari and on any non-secure
// origin, so the fallback matters — this id is the only thing grouping a
// multi-file submission, and a collision merges two strangers' rooms.
function newSubmissionId() {
  try {
    if (typeof crypto !== "undefined" && crypto.randomUUID) return crypto.randomUUID();
  } catch { /* fall through */ }
  const rand = () => Math.random().toString(36).slice(2, 10);
  return `${Date.now().toString(36)}-${rand()}-${rand()}`;
}

export default function ResultsForm() {
  const [platform, setPlatform] = useState("yahoo");
  const [teams, setTeams] = useState("12");
  const [budget, setBudget] = useState("200");
  const [format, setFormat] = useState("1qb");
  const [draftDate, setDraftDate] = useState("");
  const [note, setNote] = useState("");
  const [files, setFiles] = useState([]);

  const [phase, setPhase] = useState("idle"); // idle | uploading | success | error
  const [progress, setProgress] = useState(null);
  const [error, setError] = useState(null);
  const [sentCount, setSentCount] = useState(0);
  const fileInputRef = useRef(null);

  const busy = phase === "uploading";
  const canSubmit = !busy && files.length > 0 && draftDate.trim().length > 0;

  function pickFiles(e) {
    const picked = Array.from(e.target.files || []);
    setError(null);
    if (picked.length > MAX_FILES) {
      setError(`Up to ${MAX_FILES} files at a time — pick your best ${MAX_FILES}.`);
      setFiles([]);
      return;
    }
    const tooBig = picked.find((f) => f.size > MAX_FILE_BYTES);
    if (tooBig) {
      setError(`"${tooBig.name}" is over 4MB. Screenshot it again at a smaller size, or send the CSV.`);
      setFiles([]);
      return;
    }
    setFiles(picked);
  }

  async function submit() {
    if (!canSubmit) return;
    setPhase("uploading");
    setError(null);

    const submissionId = newSubmissionId();
    const fileCount = files.length;

    for (let i = 0; i < fileCount; i++) {
      setProgress(`Uploading ${i + 1} of ${fileCount}…`);
      const form = new FormData();
      form.append("submissionId", submissionId);
      form.append("platform", platform);
      form.append("teams", String(teams));
      form.append("budget", String(budget));
      form.append("format", format);
      form.append("draftDate", draftDate);
      form.append("note", note.slice(0, 280));
      form.append("fileIndex", String(i));
      form.append("fileCount", String(fileCount));
      form.append("source", "submit_page");
      form.append("file", files[i], files[i].name);

      let data = {};
      try {
        // No Content-Type header: the browser must set the multipart boundary.
        const resp = await fetch("/api/submit-results", { method: "POST", body: form });
        data = await resp.json().catch(() => ({}));
        if (!resp.ok || !data.ok) throw new Error(data.error || "That upload did not go through.");
      } catch (err) {
        // Stop on the first failure rather than pressing on: a partial
        // submission with a missing page is worse than none, because the record
        // is only written when the last file lands.
        setError(`${err?.message || "Upload failed."} (file ${i + 1} of ${fileCount})`);
        setProgress(null);
        setPhase("error");
        return;
      }
    }

    setSentCount(fileCount);
    setProgress(null);
    setPhase("success");
    captureResultsSubmitted({ platform, teams: Number(teams), format, fileCount });
  }

  if (phase === "success") {
    return (
      <div style={card}>
        <div style={successText}>
          Got it — {sentCount} {sentCount === 1 ? "file" : "files"} received. Your room joins the next data update.
        </div>
        <div style={fineText}>
          Results are used in aggregate only and nothing identifying is ever shown.
        </div>
      </div>
    );
  }

  const field = (id, text, control) => (
    <div>
      <label htmlFor={id} style={label}>{text}</label>
      {control}
    </div>
  );

  return (
    <div style={card}>
      <p style={{ fontSize: SZ.body, color: C.text.secondary, margin: "0 0 16px", lineHeight: 1.5 }}>
        Export or screenshot your draft results page and upload it here with your league settings. A person
        reads every submission, so any format works.
      </p>

      {/* Two columns where there is room, one on a phone — auto-fit, so no
          breakpoint has to be maintained alongside the app's other ones. */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(140px, 1fr))", gap: 12 }}>
        {field("submit-platform", "Platform",
          <select id="submit-platform" value={platform} onChange={(e) => setPlatform(e.target.value)} disabled={busy} style={input}>
            {PLATFORMS.map((p) => <option key={p.value} value={p.value}>{p.label}</option>)}
          </select>)}

        {field("submit-format", "Format",
          <select id="submit-format" value={format} onChange={(e) => setFormat(e.target.value)} disabled={busy} style={input}>
            {FORMATS.map((f) => <option key={f.value} value={f.value}>{f.label}</option>)}
          </select>)}

        {field("submit-teams", "Teams",
          <input id="submit-teams" type="number" inputMode="numeric" min={6} max={20}
            value={teams} onChange={(e) => setTeams(e.target.value)} disabled={busy} style={input} />)}

        {field("submit-budget", "Budget ($)",
          <input id="submit-budget" type="number" inputMode="numeric" min={1}
            value={budget} onChange={(e) => setBudget(e.target.value)} disabled={busy} style={input} />)}

        {field("submit-date", "Draft date",
          <input id="submit-date" type="date" value={draftDate}
            onChange={(e) => setDraftDate(e.target.value)} disabled={busy} style={input} />)}
      </div>

      <div style={{ marginTop: 12 }}>
        {field("submit-note", "Anything we should know? (optional)",
          <input id="submit-note" type="text" maxLength={280} value={note}
            onChange={(e) => setNote(e.target.value)} disabled={busy}
            placeholder="e.g. keepers were kept at last year's price" style={input} />)}
      </div>

      <div style={{ marginTop: 12 }}>
        <label htmlFor="submit-files" style={label}>
          Screenshots or spreadsheet (up to {MAX_FILES}, 4MB each)
        </label>
        <input id="submit-files" ref={fileInputRef} type="file" multiple accept={ACCEPT}
          onChange={pickFiles} disabled={busy}
          style={{ ...input, padding: 10, fontSize: SZ.small }} />
        {files.length > 0 && (
          <div style={{ marginTop: 8, fontSize: SZ.micro, color: C.text.muted, lineHeight: 1.6 }}>
            {files.map((f) => (
              <div key={f.name}>{f.name} · {Math.max(1, Math.round(f.size / 1024))} KB</div>
            ))}
          </div>
        )}
      </div>

      <div style={{ marginTop: 16, display: "flex", flexWrap: "wrap", alignItems: "center", gap: 12 }}>
        <button onClick={submit} disabled={!canSubmit} style={{ ...primaryBtn(canSubmit), flex: "1 1 160px" }}>
          {busy ? "Uploading…" : "Send my results"}
        </button>
        {progress && <span style={{ fontSize: SZ.small, color: C.text.secondary }}>{progress}</span>}
      </div>

      {error && <div style={errorText}>{error}</div>}

      <div style={fineText}>
        Results are used in aggregate only and nothing identifying is ever shown.
      </div>
    </div>
  );
}
